import React from 'react';
import { useIntl } from 'react-intl';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import BlockDataForm from '@plone/volto/components/manage/Form/BlockDataForm';
import config from '@plone/volto/registry';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import useImageCopyright from '../Image/useImageCopyright';

export const LogosSchema = ({ intl }) => ({
  title: intl.formatMessage({ id: 'Logos', defaultMessage: 'Logos' }),
  fieldsets: [{ id: 'default', title: 'Default', fields: ['logos'] }],
  properties: {
    logos: {
      title: intl.formatMessage({ id: 'Logos', defaultMessage: 'Logos' }),
      widget: 'object_list',
      schema: {
        title: intl.formatMessage({ id: 'Logo', defaultMessage: 'Logo' }),
        fieldsets: [
          { id: 'default', title: 'Default', fields: ['image', 'alt', 'href'] },
        ],
        properties: {
          image: {
            title: intl.formatMessage({ id: 'Image', defaultMessage: 'Image' }),
            widget: 'object_browser',
            mode: 'image',
            selectedItemAttrs: ['Title', 'image_field', 'image_scales'],
          },
          alt: {
            title: intl.formatMessage({
              id: 'Alternative text',
              defaultMessage: 'Alternative text',
            }),
          },
          href: {
            title: intl.formatMessage({ id: 'Link', defaultMessage: 'Link' }),
            widget: 'object_browser',
            mode: 'link',
            allowExternals: true,
            selectedItemAttrs: ['Title'],
          },
        },
        required: ['image'],
      },
    },
  },
  required: [],
});

function Logo({ item, isEditMode }) {
  const intl = useIntl();
  const asset = item.image?.[0];
  const url = asset?.['@id'];
  const copyright = useImageCopyright(
    isEditMode ? url : undefined,
    item.copyright,
  );
  const Image = config.getComponent({ name: 'Image' }).component;
  const picture = url ? (
    <Image
      item={asset.image_scales ? asset : undefined}
      src={
        asset.image_scales
          ? undefined
          : `${flattenToAppURL(url)}/@@images/image/preview`
      }
      alt={item.alt || asset.Title || asset.title || ''}
      loading="lazy"
      sizes="180px"
    />
  ) : (
    intl.formatMessage({
      id: 'Choose an image',
      defaultMessage: 'Choose an image',
    })
  );
  const href = item.href?.[0]?.['@id'];
  return (
    <li>
      <figure className="bonn-logo">
        <span className="bonn-logo-picture">
          {href && !isEditMode ? (
            <UniversalLink href={href}>{picture}</UniversalLink>
          ) : (
            picture
          )}
        </span>
        {copyright && (
          <figcaption>
            <small className="bonn-logo-copyright">© {copyright}</small>
          </figcaption>
        )}
      </figure>
    </li>
  );
}

export function LogosView({ data, isEditMode = false }) {
  const intl = useIntl();
  const logos = (data.logos || []).filter(
    (item) => isEditMode || item.image?.[0]?.['@id'],
  );
  if (!isEditMode && !logos.length) return null;
  return (
    <div className="block bonn-logos">
      {isEditMode && !logos.length && (
        <p>
          {intl.formatMessage({
            id: 'Add logos in the sidebar.',
            defaultMessage: 'Add logos in the sidebar.',
          })}
        </p>
      )}
      <ul className="bonn-logos-list">
        {logos.map((item) => (
          <Logo key={item['@id']} item={item} isEditMode={isEditMode} />
        ))}
      </ul>
    </div>
  );
}

export function LogosEdit(props) {
  const {
    data,
    block,
    selected,
    onChangeBlock,
    blocksErrors,
    navRoot,
    contentType,
  } = props;
  const intl = useIntl();
  const schema = LogosSchema({ intl });
  return (
    <>
      <LogosView data={data} isEditMode />
      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          formData={data}
          block={block}
          onChangeField={(id, value) =>
            onChangeBlock(block, { ...data, [id]: value })
          }
          onChangeBlock={onChangeBlock}
          errors={blocksErrors}
          navRoot={navRoot}
          contentType={contentType}
        />
      </SidebarPortal>
    </>
  );
}
